/**
 * Drawer — the right-hand panel B2 (new project) opens over B1, per chat1.md :659.
 *
 * A drawer rather than a modal because the list underneath is context the person is choosing
 * from, and a dialog that blanks it makes them close it to remember what they were doing.
 *
 * **Mounting is opening.** There is no `open` prop: the caller renders the drawer when it
 * wants one and stops rendering it when it does not, which is also what gives every open a
 * blank form without a reset effect. A boolean prop would keep a half-typed draft alive
 * behind a closed panel and hand it back to the next person who pressed the button.
 *
 * Focus handling is deliberately minimal and honest about it: the panel takes focus on
 * mount — unless a field inside it already took it with `autoFocus`, which React applies
 * during the commit, before this component's effect runs — the previously focused element
 * gets it back on unmount, and `Escape` closes.
 * There is no tab trap — the drawer sits above an inert-looking but still reachable list —
 * and a real one belongs with the first screen that needs a nested dialog, not here.
 */

import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';

import { Button } from './Button';
import styles from './Drawer.module.css';

export interface DrawerProps {
  /** Rendered as the panel's heading, and used as its accessible name. */
  readonly title: string;
  /** Accessible name of the ✕ button, already translated by the caller. */
  readonly closeLabel: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** The action row pinned to the bottom of the panel. */
  readonly footer?: ReactNode;
}

export function Drawer({ title, closeLabel, onClose, children, footer }: DrawerProps) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);

  // The close callback, reached through a ref so the mount effect below can have empty
  // dependencies. A caller that passes an inline arrow — every caller — would otherwise
  // re-run that effect on each render, and re-running it means re-capturing "the previously
  // focused element" as the panel itself and stealing focus back on every keystroke.
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  // The opener, read during the first render: by the time the mount effect runs, a field's
  // `autoFocus` has already moved focus into the panel, and capturing it there would hand
  // focus back to an element that unmounts with the drawer.
  const [previous] = useState(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );

  // The field a child's `autoFocus` put the caret in, remembered on the first run of the
  // effect below. Remembered rather than re-read: StrictMode's rehearsal unmount runs the
  // cleanup — which hands focus back to the opener — before the effect runs again, and a
  // re-read would then find focus outside the panel and take it for the panel itself.
  const entry = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // A child's `autoFocus` is the caller naming the field worth the caret; the panel only
    // takes focus when nothing inside it has.
    const active = document.activeElement;
    if (entry.current === null && active instanceof HTMLElement && panel.current?.contains(active)) {
      entry.current = active;
    }
    (entry.current ?? panel.current)?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKeyDown);

    // The page behind must not scroll under the panel; a drawer that moves the list it was
    // opened from loses the row the person was looking at.
    const scroll = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = scroll;
      previous?.focus();
    };
  }, [previous]);

  return (
    <div className={styles.overlay}>
      {/* The backdrop is its own element so a click on the panel never bubbles into a close.
          `mousedown` and not `click`: a drag that starts inside the panel and ends on the
          backdrop — selecting a field's text and overshooting — is not a dismissal. */}
      <div
        className={styles.backdrop}
        role="presentation"
        onMouseDown={() => {
          close.current();
        }}
      />
      <div
        ref={panel}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className={styles.header}>
          <h2 className={styles.title} id={titleId}>
            {title}
          </h2>
          {/* The label is visually hidden rather than supplied as `aria-label`: one accessible
              name, taken from real text, so a translator sees it in the same catalog as
              everything else on the screen. */}
          <Button
            variant="tertiary"
            size="sm"
            icon={<X size={16} strokeWidth={1.5} aria-hidden />}
            onClick={onClose}
          >
            <span className="sr-only">{closeLabel}</span>
          </Button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer === undefined ? null : <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
}
